import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { auth, funtions } from 'utils/firebase';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import AnimateButton from 'ui-component/extended/AnimateButton';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

// assets
import { IconLogout, IconSettings } from '@tabler/icons';

// ==============================|| PROFILE MENU ||============================== //

const SnackbarAlert = React.forwardRef(function SnackbarAlert(props, ref) {
  return <Alert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ProfileSection = () => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();

  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [open, setOpen] = React.useState(false);
  const [snackbarState, setSnackbarState] = React.useState({
    snackbarOpen: false,
    snackbarMessage: '',
    snackbarType: 'info'
  });
  const { snackbarOpen, snackbarMessage, snackbarType } = snackbarState;
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = React.useRef(null);

  const handleChangePassword = () => {
    setDialogOpen(true);
  }

  const handleCloseSnackbar = () => {
    setSnackbarState({ snackbarOpen: false, snackbarMessage: snackbarMessage, snackbarType: snackbarType });
  }

  const handleLogout = async () => {
    auth.signOut();
    navigate('/');
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const changePassword = async (password) => {
    let user = auth.currentUser;

    var result = '';
    try {
      var callable = funtions.httpsCallable('changePassword');
      result = await callable({
        'email': user.email,
        'password': password
      });

      if (result.data.result == true) {
        setDialogOpen(false);
        setSnackbarState({
          snackbarOpen: true,
          snackbarMessage: '비밀번호를 변경했습니다.',
          snackbarType: 'success'
        });
      }
      else {
        setDialogOpen(false);
        setSnackbarState({
          snackbarOpen: true,
          snackbarMessage: '비밀번호 변경에 실패하였습니다. ' + result.data.errorMessage,
          snackbarType: 'error'
        });
      }
    }
    catch (e) {
      setDialogOpen(false);
      setSnackbarState({
        snackbarOpen: true,
        snackbarMessage: '비밀번호 변경에 실패하였습니다. ' + e,
        snackbarType: 'error'
      });
    }
  };

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Snackbar
        disablePortal
        sx={{ zIndex: 10000 }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
      >
        <SnackbarAlert onClose={handleCloseSnackbar} severity={snackbarType} sx={{ width: '100%' }}>
          {snackbarMessage}
        </SnackbarAlert>
      </Snackbar>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                    <Box sx={{ px: 2, pb: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        {/* <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          selected={selectedIndex === 0}
                          onClick={(event) => handleListItemClick(event, 0, '#')}
                        >
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Account Settings</Typography>} />
                        </ListItemButton> */}
                        <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          selected={selectedIndex === 4}
                          onClick={handleChangePassword}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">비밀번호 변경</Typography>} />
                        </ListItemButton>
                        <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">로그아웃</Typography>} />
                        </ListItemButton>

                        <Dialog open={dialogOpen} onClose={handleDialogClose}>
                          <DialogTitle sx={{ mt: 1 }}><Typography variant="h4">비밀번호 변경</Typography></DialogTitle>
                          <Formik
                            initialValues={{ newPassword: '', newPasswordConfirm: '' }}
                            validationSchema={Yup.object({
                              newPassword: Yup.string().min(6, '비밀번호는 6자 이상이어야 합니다.').required('필수 입력'),
                              newPasswordConfirm: Yup.string().oneOf([Yup.ref('newPassword'), null], '비밀번호가 일치하지 않습니다.').required('필수 입력'),
                            })}
                            onSubmit={(values) => {
                              console.log(values);
                              // 제출 로직
                              changePassword(values.newPassword);
                            }}
                          >
                            {({ errors, touched }) => (
                              <Form>
                                <DialogContent>
                                  <DialogContentText sx={{ mb: 1, mt: 2 }}>
                                    변경할 비밀번호를 입력해주세요.
                                  </DialogContentText>
                                  <Field
                                    as={TextField}
                                    fullWidth
                                    label="새 비밀번호"
                                    margin="none"
                                    name="newPassword"
                                    type="password"
                                    helperText={touched.newPassword && errors.newPassword}
                                    error={touched.newPassword && Boolean(errors.newPassword)}
                                    sx={{ ...theme.typography.customInput }}
                                  />
                                  <Field
                                    as={TextField}
                                    fullWidth
                                    label="새 비밀번호 확인"
                                    margin="none"
                                    name="newPasswordConfirm"
                                    type="password"
                                    helperText={touched.newPasswordConfirm && errors.newPasswordConfirm}
                                    error={touched.newPasswordConfirm && Boolean(errors.newPasswordConfirm)}
                                    sx={{ ...theme.typography.customInput }}
                                  />
                                </DialogContent>
                                <DialogActions sx={{ mb: 2, mx: 2 }}>
                                  <AnimateButton>
                                    <Button
                                      disableElevation
                                      fullWidth
                                      size="large"
                                      type="submit"
                                      variant="contained"
                                      color="primary"
                                    >
                                      변경하기
                                    </Button>
                                  </AnimateButton>
                                  <AnimateButton>
                                    <Button
                                      style={{
                                        color: 'white',
                                        backgroundColor: theme.palette.grey[500],
                                        '&:hover': { backgroundColor: theme.palette.grey[500] }
                                      }}
                                      onClick={handleDialogClose}
                                      disableElevation
                                      fullWidth
                                      size="large"
                                      type="button"
                                      variant="contained"
                                      color="secondary"
                                    >
                                      취소하기
                                    </Button>
                                  </AnimateButton>
                                </DialogActions>
                              </Form>
                            )}
                          </Formik>
                        </Dialog>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
